import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { UserPlus, X } from 'lucide-react';
import {
  invitationsEmployeur,
  libelleDuRole,
  type InvitationEmployeur,
} from '@/services/invitationsEmployeur.service';

interface Props {
  /** L'employeur propriétaire du dossier. Absent : la section ne s'affiche pas. */
  employerUuid?: string;
}

/**
 * « Qui accède à cette entreprise » — la section d'invitation des acteurs.
 *
 * Où elle vit, et pourquoi pas ailleurs
 * -------------------------------------
 * Elle se greffe sur la FICHE DE DOSSIER, pas sur une entrée de menu : la règle
 * de Rose du 16/08/2026 est explicite — une tâche qui s'applique aux objets
 * d'une liste existante se branche sur cette liste, jamais en énième entrée de
 * menu. Le dossier est aujourd'hui le seul endroit du produit où un employeur
 * apparaît.
 *
 * Ce que la mise en forme suit
 * ----------------------------
 * Les codes de la fiche de dossier, repris tels quels : carte
 * `rounded-xl border border-neutral-200 bg-white`, titre `text-lg
 * font-semibold`, échelle d'espacement en multiples de 4. Aucun style local
 * inventé (règle 4 du standard).
 *
 * Le formulaire d'invitation ne s'affiche PAS en permanence : c'est un bouton
 * qu'on active (règle 7 — « modifier le mot de passe doit être un bouton qu'on
 * active, pas un champ affiché en permanence »).
 *
 * Les lignes sont fines, deux informations par ligne au maximum, et l'état
 * NORMAL n'est pas signalé : seule l'exception porte une pastille (règle 1 —
 * « une pastille qui dit l'état normal n'apporte rien »).
 */
export function AccesEntreprisePanel({ employerUuid }: Props) {
  const [invitations, setInvitations] = useState<InvitationEmployeur[]>([]);
  const [chargement, setChargement] = useState(true);
  const [autorise, setAutorise] = useState(true);
  const [message, setMessage] = useState<{
    ton: 'succes' | 'erreur';
    texte: string;
  } | null>(null);

  // Le retour de l'écran d'invitation porte l'adresse invitée : c'est lui qui
  // dit ce qui vient de se passer, sur la page où le résultat est visible.
  const [parametres] = useSearchParams();
  useEffect(() => {
    const invitee = parametres.get('invitee');
    if (invitee) {
      setMessage({ ton: 'succes', texte: `Invitation envoyée à ${invitee}.` });
    }
  }, [parametres]);

  const recharger = useCallback(async () => {
    if (!employerUuid) return;
    setChargement(true);
    try {
      setInvitations(await invitationsEmployeur.lister(employerUuid));
      setAutorise(true);
    } catch (erreur: unknown) {
      // 401/403 : ce n'est pas une panne, c'est la règle. On le dit.
      const statut = (erreur as { response?: { status?: number } })?.response
        ?.status;
      setAutorise(statut !== 401 && statut !== 403);
      setInvitations([]);
    } finally {
      setChargement(false);
    }
  }, [employerUuid]);

  useEffect(() => {
    void recharger();
  }, [recharger]);

  const enAttente = useMemo(
    () => invitations.filter(i => i.etat === 'pending'),
    [invitations]
  );
  const entrees = useMemo(
    () => invitations.filter(i => i.etat === 'accepted'),
    [invitations]
  );

  if (!employerUuid) return null;

  // Un cabinet n'a rien à lire ici : le serveur lui refuse la liste des
  // invitations, et le panneau « Ce que ce client reçoit » — juste au-dessus —
  // porte déjà ce qu'il PEUT faire. Laisser ce bloc affichait deux cartes qui se
  // contredisaient : l'une ouvrant le premier accès, l'autre annonçant « votre
  // cabinet n'y donne pas les droits ». Constaté sur la capture du 25/08/2026,
  // invisible dans le code.
  if (!chargement && !autorise) return null;

  const revoquer = async (uuid: string) => {
    try {
      await invitationsEmployeur.revoquer(employerUuid, uuid);
      await recharger();
    } catch {
      setMessage({
        ton: 'erreur',
        texte: "Cette invitation n'a pas pu être révoquée.",
      });
    }
  };

  return (
    <div className="shadow-soft rounded-xl border border-neutral-200 bg-white p-6">
      <div className="mb-4 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-neutral-900">
            Accès de l&apos;entreprise
          </h2>
          <p className="mt-1 text-sm text-neutral-500">
            Les personnes de l&apos;entreprise qui accèdent à cet espace, et
            avec quel rôle.
          </p>
        </div>
        {autorise && (
          <Link
            to={`/mon-entreprise/inviter?employeur=${encodeURIComponent(employerUuid)}`}
            className="inline-flex min-h-11 shrink-0 items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-opacity hover:opacity-90"
            style={{
              backgroundColor: 'var(--marque-accent)',
              color: 'var(--marque-sur-accent)',
            }}
          >
            <UserPlus className="h-4 w-4" />
            Inviter
          </Link>
        )}
      </div>

      {message && (
        <p
          className={`mb-4 rounded-lg px-4 py-2 text-sm ${
            message.ton === 'succes'
              ? 'bg-green-50 text-green-800'
              : 'bg-red-50 text-red-800'
          }`}
          role="status"
        >
          {message.texte}
        </p>
      )}

      {chargement ? (
        <p className="py-4 text-sm text-neutral-500">Chargement…</p>
      ) : !autorise ? (
        <p className="py-4 text-sm text-neutral-500">
          Les accès de cette entreprise sont gérés par l&apos;entreprise
          elle-même. Votre cabinet n&apos;y donne pas les droits.
        </p>
      ) : invitations.length === 0 ? (
        <p className="py-4 text-sm text-neutral-500">
          Personne n&apos;a encore été invité dans cette entreprise.
        </p>
      ) : (
        <div>
          {entrees.map(invitation => (
            <div
              key={invitation.uuid}
              className="flex items-center justify-between gap-4 py-2 text-sm"
            >
              <span className="truncate text-neutral-900">
                {invitation.email}
              </span>
              <span className="shrink-0 text-neutral-500">
                {libelleDuRole(invitation.role)}
              </span>
            </div>
          ))}

          {enAttente.map(invitation => (
            <div
              key={invitation.uuid}
              className="flex items-center justify-between gap-4 py-2 text-sm"
            >
              <span className="flex min-w-0 items-center gap-2">
                <span className="truncate text-neutral-900">
                  {invitation.email}
                </span>
                {/* Seule l'EXCEPTION est signalée : une invitation en attente
                    n'est pas encore un accès. */}
                <span className="shrink-0 rounded-full bg-amber-100 px-2 py-1 text-xs text-amber-800">
                  invitation en attente
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-3">
                <span className="text-neutral-500">
                  {libelleDuRole(invitation.role)}
                </span>
                <button
                  type="button"
                  onClick={() => void revoquer(invitation.uuid)}
                  title="Révoquer cette invitation"
                  aria-label={`Révoquer l'invitation de ${invitation.email}`}
                  className="flex h-6 w-6 items-center justify-center rounded text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
